import { EditorView, ViewUpdate } from "@codemirror/view"
import { Extension } from "@codemirror/state"
import { forwardRef, useImperativeHandle } from "react"
import { tcx } from "@choiceform/design-system"
import { useCodeEditor } from "./hooks"

import "./styles/style.scss"

export interface CursorPosition {
  column: number
  line: number
}

export interface CodeEditorProps {
  autoFocus?: boolean
  className?: string
  customExtensions?: Extension[]
  lineWrapping?: boolean
  onChange?(value: string, viewUpdate: ViewUpdate): void
  onCursorChange?(position: CursorPosition): void
  onEditorUpdate?: (update: ViewUpdate) => void
  onFormat?: (formattedCode: string) => void
  onSelectionChange?(characterCount: number): void
  placeholder?: string | HTMLElement
  readonly?: boolean
  value?: string
}

export interface CodeEditorRef {
  getCursorPosition: () => CursorPosition | null
  getEditor: () => EditorView | null
  getSelectedCharacterCount: () => number
}

export const CodeEditor = forwardRef<CodeEditorRef, CodeEditorProps>((props, ref) => {
  const { 
    value, 
    onChange, 
    autoFocus, 
    className, 
    readonly, 
    lineWrapping,
    onEditorUpdate, 
    onFormat, 
    onCursorChange,
    onSelectionChange,
  } = props

  const { containerRef, editorViewRef, getCursorPosition, getSelectedCharacterCount } = useCodeEditor({
    value,
    onChange,
    autoFocus,
    readonly,
    lineWrapping,
    onEditorUpdate,
    onFormat,
    onCursorChange,
    onSelectionChange,
  })

  useImperativeHandle(ref, () => ({
    getCursorPosition,
    getEditor: () => editorViewRef.current,
    getSelectedCharacterCount,
  }), [getCursorPosition, editorViewRef, getSelectedCharacterCount])

  return (
    <div
      ref={containerRef}
      className={tcx("relative h-full", className)}
    />
  )
})

CodeEditor.displayName = "CodeEditor"
